import { useCallback, useEffect, useMemo, useState } from 'react';
import { GridColDef, GridPreProcessEditCellProps, GridSelectionModel } from '@mui/x-data-grid-pro';
import useSWR from 'swr';
import { validate as isValidUUID } from 'uuid';
import { FormControlLabel, Checkbox } from '@mui/material';
import { dataGridCol, gridBooleanCell, renderBooleanCell } from '@shared/components/grid/common';
import { PhoenixGrid } from '@shared/components/grid/PhoenixGrid';
import { RelativeWrapper } from '@shared/styledComponents';
import { LocalTabContent } from '@styles/CommonStyles';
import { IOption } from '@shared/interfaces';
import { GridColSelect } from '@shared/components/grid/GridColSelect';
import { GridColNumber } from '@shared/components/grid/GridColNumber';
import { deCamelize, formatCurrency } from '@shared/commonFunctions';
import { GridColBoolean } from '@shared/components/grid/GridColBoolean';
import { OPERATOR_SYMBOL, operatorsList, OperatorType } from '@shared/constants/operatorsList';
import { ILenderUtilityTabProps } from '../LenderUtilityTabs';
import { PhoenixGridHeader } from '../../shared/PhoenixGridHeader';
import { IAutoSubmitConfigurationGrid, TAutoSubmitConfigurationType } from '../../interfaces';
import { requestUpdateAutoSubmitEligible } from '../requests';
import { requestCreateAutoSubmit, requestUpdateAutoSubmit } from './queries';
import { lenderUtilityAutoSubmitFormSchema } from './validation';

type TAutoSubmitFieldType = 'Text' | 'Boolean';

interface IAutoSubmit {
  active: boolean;
  autoSubmitType: string;
  fieldType: TAutoSubmitFieldType;
}

type AutoSubmitType = 
  | 'DTI' 
  | 'PTI' 
  | 'MaximumFrontEnd' 
  | 'MaximumAmountFinanced' 
  | 'MaximumBackEnd' 
  | 'MinimumAmountFinanced' 
  | 'MinimumMonthsOnReport' 
  | 'MinimumPaymentsMadeOnCurrentAuto' 
  | 'MinimumLengthOfEmployment' 
  | 'TimeSinceBk' 
  | 'TimeAtCurrentAddress' 
  | 'MinimumMonthlyIncome' 
  | 'MinimumDisposableIncome'
  | 'OpenBk';

export const getPrefix = (type: AutoSubmitType | string): string | undefined => {
  switch (type) {
    case 'MaximumFrontEnd':
    case 'MaximumAmountFinanced':
    case 'MaximumBackEnd':
    case 'MinimumAmountFinanced':
    case 'MinimumMonthlyIncome':
    case 'MinimumDisposableIncome':
      return '$';
    default:
      return undefined;
  }
};

export const getSuffix = (type: AutoSubmitType | string): string | undefined => {
  switch (type) {
    case 'DTI':
    case 'PTI':
      return '%';
    case 'MinimumMonthsOnReport':
    case 'MinimumPaymentsMadeOnCurrentAuto':
    case 'MinimumLengthOfEmployment':
    case 'TimeSinceBk':
    case 'TimeAtCurrentAddress':
      return ' month(s)';
    default:
      return undefined;
  }
};

const getList = (autoSubmit: IOption<string | number>[], data?: TAutoSubmitConfigurationType[]) => {
  return autoSubmit.map((item) => {
    const existingRule = data?.find((rule) => rule?.autoSubmit?.autoSubmitType === item.value);
    return {
      id: existingRule?.id || item.value.toString(),
      active: existingRule?.active || false,
      autoSubmit: existingRule?.autoSubmit.autoSubmitType || item.value,
      condition: existingRule?.condition || '',
      value: existingRule?.value as number | boolean,
    };
  });
};

export const AutoSubmit = ({ data, lenderId }: ILenderUtilityTabProps) => {
  const { data: autoSubmitTypes = [] } = useSWR<IAutoSubmit[]>('/lender-service/lenders/autoSubmit');
  const [autoSubmitTypeList, setAutoSubmitTypeList] = useState<IAutoSubmitConfigurationGrid[]>([]);
  const [selectionModel, setSelectionModel] = useState<GridSelectionModel>([]);
  const [autoSubmitApp, setAutoSubmitApp] = useState(data?.autoSubmitLoanApplication ?? false);
  const [updating, setUpdating] = useState(false);

  const existingRules = new Set();

  const booleanRules = new Set(
    autoSubmitTypes.filter((rule) => rule.fieldType === 'Boolean').map((rule) => rule?.autoSubmitType),
  );

  const isBooleanField = (value?: string) => value && booleanRules.has(value);
    
  const lenderAutoSubmit = useMemo(
    () => (data?.autoSubmitConfiguration ?? []).map((rule) => ({
      ...rule,
      active: rule.active,
      autoSubmit: rule?.autoSubmit?.autoSubmitType,
      isBooleanField: isBooleanField(rule?.autoSubmit?.autoSubmitType)
    })),
    [data?.autoSubmitConfiguration, isBooleanField]
  );

  lenderAutoSubmit?.forEach(({ autoSubmit }) => {
    existingRules.add(autoSubmit);
  });

  const autoSubmit: IOption<string | number>[] = useMemo(
    () =>
      autoSubmitTypes.map((type) => ({
        value: type?.autoSubmitType,
        label: deCamelize(type?.autoSubmitType),
        disabled: existingRules.has(type?.autoSubmitType),
      })),
    [autoSubmitTypes, existingRules],
  );
    
  const preProcessCondition = ({ props, otherFieldsProps }: GridPreProcessEditCellProps) => ({
    ...props,
    value: isBooleanField(otherFieldsProps?.autoSubmit?.value) ? OperatorType.Equal : props.value,
  });

  const preProcessIsBoolean = ({ props, otherFieldsProps }: GridPreProcessEditCellProps) => ({
    ...props,
    value: isBooleanField(otherFieldsProps?.autoSubmit?.value),
  });

  const columns: GridColDef[] = [
    dataGridCol('active', {
      ...gridBooleanCell,
      headerName: 'Active',
      minWidth: 180,
      flex: 1,
    }),
    dataGridCol('autoSubmit', {
      headerName: 'Type',
      flex: 1,
      editable: false,
      renderCell: (params) => deCamelize(params.value),
    }),
    dataGridCol('condition', {
      required: true,
      headerName: 'Condition',
      flex: 1,
      preProcessEditCellProps: preProcessCondition,
      renderEditCell: (params) => (
        <GridColSelect
          params={params}
          options={
            booleanRules.has(params.row?.autoSubmit)
              ? [{ value: OperatorType.Equal, label: OPERATOR_SYMBOL[OperatorType.Equal] }]
              : operatorsList
            }
        />
      ),
    }),
    dataGridCol('value', {
      headerName: 'Value',
      flex: 1,
      valueGetter: (params) => (isBooleanField(params.row?.autoSubmit) ? params.value === 'true' ? true : false : params.value),
      renderCell: (params) => (isBooleanField(params.row?.autoSubmit) 
                                ? renderBooleanCell(params) 
                                : getPrefix(params.row?.autoSubmit) === '$' 
                                ? params.value && formatCurrency(params.value) 
                                : getSuffix(params.row?.autoSubmit) === '%'
                                ? params.value && `${parseFloat(params.value).toFixed(2)}${params.value && getSuffix(params.row?.autoSubmit)}` 
                                : getSuffix(params.row?.autoSubmit) === ' month(s)' 
                                ? params.value && `${params.value}${params.value && getSuffix(params.row?.autoSubmit)}`
                                : params.value),
      renderEditCell: (params) =>
        isBooleanField(params.row.autoSubmit) ? (
          <GridColBoolean 
            params={params} 
            disabled={!autoSubmitApp && !params.row.active} 
          />
        ) : (
          <>
            {getPrefix(params.row.autoSubmit) && (<span>{getPrefix(params.row.autoSubmit)}</span>)}
            <GridColNumber 
              params={params} 
              limit={9} 
              disabled={!autoSubmitApp && !params.row.active} 
              required={params.row.active} 
              decimalScale={params.row.autoSubmit == 'DTI' || params.row.autoSubmit == 'PTI' ? 2 : 0} 
            />
            {getSuffix(params.row.autoSubmit) && (<span>{getSuffix(params.row.autoSubmit)}</span>)}
          </>
        ),
    }),
    dataGridCol('isBooleanField', {
      hide: true,
      preProcessEditCellProps: preProcessIsBoolean,
      valueGetter: ({row}) => isBooleanField(row.autoSubmit),
    })
  ].map((column) => ({ editable: true, ...column }));

  const gridActions = {
    onSave: (row: TAutoSubmitConfigurationType) => 
      !isValidUUID(row.id) ? requestCreateAutoSubmit(lenderId, row) : requestUpdateAutoSubmit(lenderId, row)
  };

  useEffect(() => {
    if (data?.autoSubmitConfiguration && autoSubmit) {
      const listItems = getList(autoSubmit, data.autoSubmitConfiguration);
      if (JSON.stringify(listItems) !== JSON.stringify(autoSubmitTypeList)) {
        setAutoSubmitTypeList(listItems);
      }
  
      const initialSelectedRows = listItems.filter((row) => row.active).map((row) => row.id);
      if (JSON.stringify(initialSelectedRows) !== JSON.stringify(selectionModel)) {
        setSelectionModel(initialSelectedRows);
      }
    }
  }, [data?.autoSubmitConfiguration, autoSubmit, autoSubmitTypeList, selectionModel]);

  const onChecked = useCallback(async (checked: boolean) => {
    setUpdating(true);
    setAutoSubmitApp(checked);
    await requestUpdateAutoSubmitEligible(lenderId, checked);
    setUpdating(false);
  }, [autoSubmitApp]);
  
  return (
    <RelativeWrapper>
      <LocalTabContent>
        <PhoenixGridHeader 
          title="Auto Submit" 
          subtitle="Define auto-submit rules for lender." 
          justify="flex-start" 
        />

        <FormControlLabel 
          label='Lender Eligible for Auto Submit' 
          sx={{ 
            display: 'inline-flex', 
            width: 'fit-content'
          }}
          control={
            <Checkbox
              checked={autoSubmitApp}
              onChange={(_, checked) => onChecked(checked)}
            />
          }
        />

        <PhoenixGrid
          autoHeight          
          name="Auto Submit"
          gridId={`AutoSubmit-${lenderId}`}
          pagination={false}
          columns={columns}
          loading={!lenderAutoSubmit || updating}
          selectionModel={!autoSubmitApp || updating ? [] : selectionModel}
          rows={!autoSubmitApp || updating ? [] : autoSubmitTypeList}
          canNotCopy={true}
          actions={gridActions}
          getRowId={(row) => row.id}
          disabled={!autoSubmitApp}
          validateSchema={lenderUtilityAutoSubmitFormSchema}
        />
      </LocalTabContent>
    </RelativeWrapper>
  );
};